// src/components/AdminPanel/AdminPanel.js
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import useNoIndex from "../../hooks/useNoIndex";

// Import each tab component
import AdminSummary from "./AdminSummary";
import AdminVenues from "./AdminVenues";
import AdminUsers from "./AdminUsers";
import AdminUserGatherings from "./AdminUserGatherings";
import AdminUserSlots from "./AdminUserSlots";
import BlogAdmin from "./BlogAdmin";
import PersonaAdmin from "./PersonaAdmin";
import AdminAeo from "./AdminAeo";
import AdminSocials from "./AdminSocials";
import AdminAds from "./AdminAds";
import AdminThemeLandingPages from "./AdminThemeLandingPages";
import AdminGatheringAvailability from "./AdminGatheringAvailability";
import AdminThemes from './AdminThemes';
import AdminLocations from './AdminLocations';
import AdminUsageData from './AdminUsageData';
import AdminSignupAnalytics from './AdminSignupAnalytics';
import AdminReferralAnalytics from './AdminReferralAnalytics';
import AdminInviteLinkAnalytics from './AdminInviteLinkAnalytics';
import AdminConnectionsAnalytics from './AdminConnectionsAnalytics';
import AdminLandingPagePerformance from './AdminLandingPagePerformance';
import AdminMeetupGroupFinance from './AdminMeetupGroupFinance';
import AdminMeetupGroupMappings from './AdminMeetupGroupMappings';
import AdminEmailCampaigns from './AdminEmailCampaigns';
import AdminFaqs from './AdminFaqs';
import AdminSubscriptions from './AdminSubscriptions';
import AdminPaymentIssues from './AdminPaymentIssues';

// Most-used tabs, always visible. Everything else lives under the
// collapsible "Other" group below so the sidebar isn't 16 buttons deep.
const TOP_TABS = [
  { id: "themes", label: "Themes 🏷️" },
  { id: "themeLandingPages", label: "Landing Pages 🎨" },
  { id: "landingPagePerformance", label: "Landing Page Performance 📈" },
  { id: "meetupGroupFinance", label: "Meetup Group Finance 💰" },
  { id: "meetupGroupMappings", label: "Meetup Group Mappings 🔗" },
  { id: "subscriptions", label: "Subscriptions 💳" },
  { id: "paymentIssues", label: "Payment Issues ⚠️" },
  { id: "users", label: "Users 👥" },
  { id: "inviteLinks", label: "Invite Links 🔗" },
  { id: "connectionsAnalytics", label: "Connections & Hangouts 🤝" },
];

const OTHER_TABS = [
  { id: "summary", label: "Summary 📊" },
  { id: "usageData", label: "Usage Data 📈" },
  { id: "signupAnalytics", label: "Signup Analytics 🎟️" },
  { id: "gatherings", label: "Gatherings 🎯" },
  { id: "venues", label: "Venues 📍" },
  { id: "locations", label: "Locations 🗺️" },
  { id: "userSlots", label: "User-Slots 🎟️" },
  { id: "gatheringAvailability", label: "Gathering Availability 📅" },
  { id: "referrals", label: "Referrals 🎁" },
  { id: "emailCampaigns", label: "Email Campaigns 📧" },
  { id: "faqs", label: "FAQs ❓" },
];
const OTHER_TAB_IDS = new Set(OTHER_TABS.map((t) => t.id));

// Everything about getting OkGather found/recommended - AI answer engines,
// search, and social. Grouped separately from "Other" since it's a distinct
// growing area of work, not miscellaneous admin tooling.
const GROWTH_TABS = [
  { id: "blog", label: "SEO / Blog ✍️" },
  { id: "personas", label: "Personas 🎭" },
  { id: "aeo", label: "AEO / Reddit 🤖" },
  { id: "socials", label: "Socials 📱" },
  { id: "ads", label: "Ads 📢" },
];
const GROWTH_TAB_IDS = new Set(GROWTH_TABS.map((t) => t.id));

const AdminPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [adminToken, setAdminToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true); // Add loading state
  // Starts collapsed on mobile (matches the 768px breakpoint the sidebar's
  // own mobile CSS uses) - on a narrow screen the sidebar is a fixed
  // overlay, not a squeezed-in column, so left open by default it covers
  // most of the viewport and hides whatever tab content loaded underneath.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth > 768
  );
  // Starts expanded if the page loaded directly on a tab that lives inside
  // "Other" (e.g. a bookmarked ?tab=blog link), so the active tab is never
  // hidden behind a collapsed group with no indication it's selected.
  const [otherExpanded, setOtherExpanded] = useState(
    () => OTHER_TAB_IDS.has(searchParams.get("tab"))
  );
  const [growthExpanded, setGrowthExpanded] = useState(
    () => GROWTH_TAB_IDS.has(searchParams.get("tab"))
  );
  const navigate = useNavigate();

  // Themes dirty-state guard
  const themesIsDirtyRef = useRef(false);
  const [showThemesDirtyWarning, setShowThemesDirtyWarning] = useState(false);
  const pendingTabNameRef = useRef(null);

  // Prevent search engines from indexing this admin-only page
  useNoIndex();

  // Get current tab from URL, default to "subscriptions" if not specified
  const currentTab = searchParams.get("tab") || "subscriptions";

  // Function to change tab and update URL — guarded when Themes has unsaved edits
  const changeTab = (tabName) => {
    // Hide the sidebar once a destination is picked, so the dashboard below
    // gets the full width/height it's designed for instead of staying
    // squeezed (or, on mobile, staying covered by the overlay) after
    // navigating away.
    setSidebarOpen(false);
    if (OTHER_TAB_IDS.has(tabName)) setOtherExpanded(true);
    if (GROWTH_TAB_IDS.has(tabName)) setGrowthExpanded(true);
    if (currentTab === 'themes' && themesIsDirtyRef.current && tabName !== 'themes') {
      pendingTabNameRef.current = tabName;
      setShowThemesDirtyWarning(true);
    } else {
      setSearchParams({ tab: tabName });
    }
  };

  useEffect(() => {
    // Retrieve and verify token from localStorage
    const verifyToken = async () => {
      const storedToken = localStorage.getItem("memberstackToken");

      if (!storedToken) {
        // No token found, redirect to login
        navigate("/admin-login");
        return;
      }

      try {
        // Verify the token with the backend
        const response = await fetch("/admin/verify", {
          method: "GET",
          headers: { Authorization: `Bearer ${storedToken}` },
          credentials: "include",
        });

        if (response.ok) {
          // Token is valid, set it
          setAdminToken(storedToken);
        } else {
          // Token is invalid or expired
          console.log("Token verification failed, redirecting to login");
          localStorage.removeItem("memberstackToken");
          navigate("/admin-login");
        }
      } catch (error) {
        console.error("Error verifying token:", error);
        localStorage.removeItem("memberstackToken");
        navigate("/admin-login");
      } finally {
        // Verification complete (success or failure)
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [navigate]);

  // Show loading state while verifying token
  if (isVerifying) {
    return (
      <div className="admin-panel-page">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Only render full panel if token is verified
  if (!adminToken) {
    return null; // Will redirect to login, so don't render anything
  }

  return (
    <div className="admin-panel-page">
      {/* Shown only while the sidebar is hidden, since its own toggle button
          hides along with it - this is the only way back to expand it */}
      {!sidebarOpen && (
        <button
          type="button"
          className="admin-sidebar-reopen"
          onClick={() => setSidebarOpen(true)}
          title="Show navigation"
        >
          ☰ Menu
        </button>
      )}

      {/* Unsaved Themes changes warning (triggered by tab navigation) */}
        {showThemesDirtyWarning && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: '#fff', borderRadius: '12px', padding: '28px 32px',
              maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}>
              <h3 style={{ margin: '0 0 10px', fontSize: '1.15rem', fontWeight: '700' }}>
                Unsaved Changes
              </h3>
              <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '0.92rem', lineHeight: 1.5 }}>
                You have unsaved changes to a theme. If you switch tabs now your changes will be lost.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowThemesDirtyWarning(false); pendingTabNameRef.current = null; }}
                  style={{
                    padding: '8px 18px', borderRadius: '8px', border: '1.5px solid #d1d5db',
                    backgroundColor: '#fff', color: '#374151', fontWeight: '600',
                    fontSize: '0.9rem', cursor: 'pointer',
                  }}
                >
                  Keep Editing
                </button>
                <button
                  onClick={() => {
                    const tab = pendingTabNameRef.current;
                    pendingTabNameRef.current = null;
                    themesIsDirtyRef.current = false;
                    setShowThemesDirtyWarning(false);
                    if (tab) setSearchParams({ tab });
                  }}
                  style={{
                    padding: '8px 18px', borderRadius: '8px', border: 'none',
                    backgroundColor: '#dc2626', color: '#fff', fontWeight: '600',
                    fontSize: '0.9rem', cursor: 'pointer',
                  }}
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        )}

      <div className="admin-panel-shell">
        {/* Sidebar navigation - collapsible so dashboards below get the full width */}
        <aside className={`admin-sidebar ${sidebarOpen ? "" : "admin-sidebar-collapsed"}`}>
          <button
            type="button"
            className="admin-sidebar-toggle"
            onClick={() => setSidebarOpen(false)}
            title="Hide navigation"
          >
            ☰ Hide Menu
          </button>
          <nav className="admin-nav">
            {TOP_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`admin-nav-button ${currentTab === tab.id ? "active" : ""}`}
                onClick={() => changeTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}

            <button
              type="button"
              className="admin-nav-group-toggle"
              onClick={() => setGrowthExpanded((open) => !open)}
              aria-expanded={growthExpanded}
            >
              {growthExpanded ? "▾" : "▸"} Growth
            </button>

            {growthExpanded && GROWTH_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`admin-nav-button admin-nav-button-nested ${currentTab === tab.id ? "active" : ""}`}
                onClick={() => changeTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}

            <button
              type="button"
              className="admin-nav-group-toggle"
              onClick={() => setOtherExpanded((open) => !open)}
              aria-expanded={otherExpanded}
            >
              {otherExpanded ? "▾" : "▸"} Other
            </button>

            {otherExpanded && OTHER_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`admin-nav-button admin-nav-button-nested ${currentTab === tab.id ? "active" : ""}`}
                onClick={() => changeTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main column: dashboards get almost the entire width/height now,
            with the page title tucked out of the way in the corner */}
        <div className="admin-panel-main">
          <div className="admin-panel-topbar">
            <h2 className="admin-panel-header">Admin Panel</h2>
          </div>

          <div className="admin-panel-content">
          {currentTab === "summary" && (
            <AdminSummary adminToken={adminToken} tabName="Summary" />
          )}
          {currentTab === "usageData" && (
            <AdminUsageData adminToken={adminToken} tabName="UsageData" />
          )}
          {currentTab === "signupAnalytics" && (
            <AdminSignupAnalytics adminToken={adminToken} tabName="Signup Analytics" />
          )}
          {currentTab === "gatherings" && (
            <AdminUserGatherings
              adminToken={adminToken}
              tabName="Gatherings"
            />
          )}
          {currentTab === "venues" && (
            <AdminVenues adminToken={adminToken} tabName="Venues Management" />
          )}
          {currentTab === "locations" && (
            <AdminLocations adminToken={adminToken} />
          )}
          {currentTab === "users" && (
            <AdminUsers adminToken={adminToken} tabName="Users" />
          )}
          {currentTab === "userSlots" && (
            <AdminUserSlots adminToken={adminToken} tabName="User-Slots" />
          )}
          {currentTab === "aeo" && (
            <AdminAeo tabName="AEO / Reddit" />
          )}
          {currentTab === "blog" && (
            <BlogAdmin adminToken={adminToken} tabName="Blog Management" />
          )}
          {currentTab === "personas" && (
            <PersonaAdmin adminToken={adminToken} tabName="Personas" />
          )}
          {currentTab === "socials" && (
            <AdminSocials adminToken={adminToken} tabName="Socials" />
          )}
          {currentTab === "ads" && (
            <AdminAds tabName="Ads" />
          )}
          {currentTab === "themeLandingPages" && (
            <AdminThemeLandingPages adminToken={adminToken} tabName="ThemeLandingPages" />
          )}
          {currentTab === "gatheringAvailability" && (
            <AdminGatheringAvailability adminToken={adminToken} tabName="GatheringAvailability" />
          )}
          {currentTab === "themes" && (
            <AdminThemes
              adminToken={adminToken}
              tabName="Theme Management"
              onTabChange={changeTab}
              onDirtyChange={(dirty) => { themesIsDirtyRef.current = dirty; }}
            />
          )}
          {currentTab === "referrals" && (
            <AdminReferralAnalytics adminToken={adminToken} tabName="Referral Analytics" />
          )}
          {currentTab === "inviteLinks" && (
            <AdminInviteLinkAnalytics adminToken={adminToken} tabName="Invite Links Analytics" />
          )}
          {currentTab === "emailCampaigns" && (
            <AdminEmailCampaigns adminToken={adminToken} tabName="Email Campaigns" />
          )}
          {currentTab === "faqs" && (
            <AdminFaqs adminToken={adminToken} />
          )}
          {currentTab === "connectionsAnalytics" && (
            <AdminConnectionsAnalytics adminToken={adminToken} tabName="Connections & Hangouts" />
          )}
          {currentTab === "subscriptions" && (
            <AdminSubscriptions adminToken={adminToken} tabName="Subscriptions" />
          )}
          {currentTab === "paymentIssues" && (
            <AdminPaymentIssues adminToken={adminToken} tabName="Payment Issues" />
          )}
          {currentTab === "landingPagePerformance" && (
            <AdminLandingPagePerformance adminToken={adminToken} tabName="Landing Page Performance" />
          )}
          {currentTab === "meetupGroupFinance" && (
            <AdminMeetupGroupFinance adminToken={adminToken} tabName="Meetup Group Finance" />
          )}
          {currentTab === "meetupGroupMappings" && (
            <AdminMeetupGroupMappings adminToken={adminToken} tabName="Meetup Group Mappings" />
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;